import { serialize } from 'object-to-formdata';
import { useRecoilValue } from 'recoil';
import { setExtra, captureException } from '@sentry/browser';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';

import {
  JobAttributes,
  JobResponse,
  JobRequestStep1,
  JobRequestStep2,
  JobRequestStep3,
} from 'lib/models/job';
import { parseResponse } from 'lib/utils/parser';
import { get, http } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import { useNotification } from './useNotification';

export enum ConfirmationButtonResponse {
  SAVE = 0,
  DONT_SAVE = 1,
}

interface ReturnType {
  getCompanyJobState: (pathname: string) => Promise<JobAttributes>;
  createJob: ({
    touchpoint,
  }: {
    touchpoint: JobRequestStep1;
  }) => Promise<JobAttributes | undefined>;
  updateJobStep1: ({
    touchpoint,
    id,
  }: {
    touchpoint: JobRequestStep1;
    id: string;
  }) => Promise<JobAttributes | undefined>;
  updateJobStep2: ({
    touchpoint,
    id,
  }: {
    touchpoint: JobRequestStep2;
    id: string;
  }) => Promise<JobAttributes | undefined>;
  updateJobStep3: ({
    touchpoint,
    id,
  }: {
    touchpoint: JobRequestStep3;
    id: string;
  }) => Promise<JobAttributes | undefined>;
}

export const useCompanyCampaigns = (): ReturnType => {
  const notificationInstance = useNotification();
  const { headers, plainHeaders } = useRecoilValue(httpHeadersState);

  async function getCompanyJobState(pathname: string): Promise<JobAttributes> {
    const response = await get<JobResponse>(pathname, headers);
    const job = parseResponse<JobAttributes>(response);

    return job;
  }

  const createJob = async ({ touchpoint }: { touchpoint: JobRequestStep1 }) => {
    const body = {
      touchpoint,
    };
    const formData = serialize(body, { indices: false });
    try {
      const response = await http<JobResponse>(API_ROUTES.COMPANY_TOUCHPOINTS, {
        method: 'POST',
        body: formData,
        headers: plainHeaders,
      });
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('createJob body', JSON.stringify(body));
      captureException(error);
    }
  };

  const updateJobStep1 = async ({
    touchpoint,
    id,
  }: {
    touchpoint: JobRequestStep1;
    id: string;
  }) => {
    const body = {
      touchpoint,
    };

    const formData = serialize(body, {
      indices: false,
      allowEmptyArrays: true,
    });
    try {
      const response = await http<JobResponse>(
        `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateJobStep1 body', JSON.stringify(body));
      captureException(error);
    }
  };

  const updateJobStep2 = async ({
    touchpoint,
    id,
  }: {
    touchpoint: JobRequestStep2;
    id: string;
  }) => {
    const body = {
      touchpoint,
    };

    const formData = serialize(body, {
      indices: false,
      allowEmptyArrays: true,
    });
    try {
      const response = await http<JobResponse>(
        `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateJobStep2 body', JSON.stringify(body));
      captureException(error);
    }
  };

  const updateJobStep3 = async ({
    touchpoint,
    id,
  }: {
    touchpoint: JobRequestStep3;
    id: string;
  }) => {
    const { questions_attributes, removedCustomQuestionIds } = touchpoint;
    const body = {
      touchpoint: {
        ...touchpoint,
        questions_attributes: [
          ...(questions_attributes?.map((ques) => ({
            ...ques,
            ...(!ques.isNew ? { id: ques.id } : { id: undefined }),
          })) ?? []),
          ...(removedCustomQuestionIds?.map((id) => ({
            id: id,
            _destroy: true,
          })) ?? []),
        ],
        notify_creatable: true,
      },
    };
    const formData = serialize(body, {
      indices: false,
      allowEmptyArrays: true,
    });
    try {
      const response = await http<JobResponse>(
        `${API_ROUTES.COMPANY_TOUCHPOINTS}/${id}`,
        {
          method: 'PATCH',
          body: formData,
          headers: plainHeaders,
        }
      );
      return parseResponse(response);
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
      setExtra('updateJobStep3 body', JSON.stringify(body));
      captureException(error);
    }
  };

  return {
    getCompanyJobState,
    createJob,
    updateJobStep1,
    updateJobStep2,
    updateJobStep3,
  };
};
